<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, Popover, PopoverButton, PopoverGroup, PopoverPanel } from '@headlessui/vue'
import { products } from '@/data/products.js'
import gsap from 'gsap'
import CustomLink from '@/Components/CustomLink.vue'
import Button from '@/Components/Button.vue';

const mobileMenuOpen = ref(false)
const popoverOpen = ref(false)
const productsOpen = ref(false)

// Scroll animation variables
const scrollY = ref(0)
const isScrolled = ref(false)
const showNavbar = ref(true)
const headerHeight = ref(0)
let lastScrollPosition = 0

// Add mouse position tracking for top-of-screen detection
const isMouseNearTop = ref(false)

// Function to handle mouse movement
const handleMouseMove = (event) => { isMouseNearTop.value = event.clientY <= 50; }

// Function to handle scroll events
const onScroll = () => {
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop
    scrollY.value = currentScrollPosition
    
    // Set scrolled state based on scroll position (for glass effect)
    isScrolled.value = currentScrollPosition > 20
    
    // Detect section background when scrolled
    if (isScrolled.value) {
      detectSectionBackground()
    } else {
      currentSectionBg.value = 'light' // Default when at top
    }
    
    // Keep showNavbar always true to prevent hiding
    showNavbar.value = true
    
    lastScrollPosition = currentScrollPosition
}

// Set up and clean up scroll event listener
onMounted(() => {
    window.addEventListener('scroll', onScroll)
    window.addEventListener('mousemove', handleMouseMove)
    const headerEl = document.querySelector('header')
    if (headerEl) { headerHeight.value = headerEl.offsetHeight }
    
    // Force initial scroll check
    onScroll()
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('mousemove', handleMouseMove)
})

// Add new reactive variables for dynamic background detection
const currentSectionBg = ref('light') // 'light' or 'dark'

// Function to detect background color of current section
const detectSectionBackground = () => {
  const headerHeight = 80 // Approximate header height
  const checkPoint = headerHeight + 50 // Point to check below header
  
  // Get element at the check point
  const elementBelow = document.elementFromPoint(window.innerWidth / 2, checkPoint)
  
  if (elementBelow) {
    // Find element with headerflip class
    let currentElement = elementBelow
    let attempts = 0
    const maxAttempts = 15
    
    while (currentElement && attempts < maxAttempts) {
      const classList = currentElement.classList
      
      // Check for headerflip class
      if (classList.contains('headerflip')) {
        currentSectionBg.value = 'dark'
        return
      }
      
      currentElement = currentElement.parentElement
      attempts++
    }
  }
  
  // Default to light if no headerflip class detected
  currentSectionBg.value = 'light'
}

// Add missing rotateChevron function and ref
const chevronRef = ref(null)

function rotateChevron(degrees, duration = 0.3) {
  if (chevronRef.value) {
    gsap.to(chevronRef.value, {
      rotation: degrees,
      duration: duration,
      ease: "power2.out"
    });
  }
}

// Function to animate solution items when popover opens
function animateSolutionItems() {
  nextTick(() => {
    const solutionItems = document.querySelectorAll('.solution-item');
    const ctaItems = document.querySelectorAll('.popover-cta-item');
    
    if (solutionItems.length > 0) {
      // Set initial states - items start invisible and slightly below
      gsap.set(solutionItems, { opacity: 0, y: 15 });
      gsap.set(ctaItems, { opacity: 0, y: 15 });
      
      // Animate solution items with stagger - each item appears after the previous
      gsap.to(solutionItems, { 
        opacity: 1, 
        y: 0,
        duration: 0.3, 
        stagger: 0.08, // Increased stagger for more noticeable effect
        ease: "power2.out",
        delay: 0.1 // Small delay after panel starts moving
      });
      
      // Animate CTA items after all solution items
      gsap.to(ctaItems, { 
        opacity: 1, 
        y: 0,
        duration: 0.3, 
        stagger: 0.1,
        ease: "power2.out",
        delay: 0.1 + (solutionItems.length * 0.08) // Start after solution items
      });
    }
  });
}

// Function to handle popover enter animation
function handlePopoverEnter() {
  // Start item animations early in the panel transition
  setTimeout(() => animateSolutionItems(), 100);
}

// Watch for popover state changes
watch(popoverOpen, (isOpen) => {
  if (isOpen) { 
    // Trigger animation when popover opens
    setTimeout(() => animateSolutionItems(), 250);
  }
})

// Watch for popover state changes and rotate chevron
watch(popoverOpen, (isOpen) => {
  rotateChevron(isOpen ? 180 : 0);
  
  if (isOpen) { 
    // Trigger animation when popover opens
    setTimeout(() => animateSolutionItems(), 250);
  }
})

function handleProductsButtonClick() {
  productsOpen.value = !productsOpen.value
  
  if (productsOpen.value) {
    // Wait for the panel to render before animating
    nextTick(() => {
      // Select both product items and CTA links
      const mobileItems = document.querySelectorAll('.mobile-product-item, .mobile-cta-item')
      // Ensure items start invisible
      gsap.set(mobileItems, { opacity: 0, y: 20, scale: 0.95 })
      gsap.to(mobileItems, { opacity: 1, y: 0, scale: 1, duration: 0.4, ease: "back.out(1.7)", stagger: 0.1, delay: 0.2 })
    })
  }
}

// Filter products for the header display
const headerProducts = computed(() => {
    return products
        .filter(product => product.meta.showInHeader)
        .slice(0, 6) // Limit to maximum 6 items for header display
})

// Call-to-action items
const callsToAction = [
  { name: 'Bekijk alle projecten', href: '/products' },
  { name: 'Vraag een demo aan', href: '/contact#contactForm' }
]

// Function to get safe color classes for products with fallback
function getProductIconColor(product) {
  // Try to use the actual project color from the product data
  if (product.style && product.style.projectColor) {
    // Convert the project color to a color value (e.g., 'azure-600' -> 'azure-600')
    return product.style.projectColor;
  }
  
  // Fallback to azure if no project color is available
  return 'azure-500';
}

// Function to animate mobile menu opening
function animateMobileMenuOpen() {
  const menuPanel = document.querySelector('.mobile-menu-panel');
  const menuItems = document.querySelectorAll('.mobile-menu-item');
  const logo = document.querySelector('.mobile-menu-logo');
  const closeButton = document.querySelector('.mobile-menu-close');
  const backdrop = document.querySelector('.mobile-menu-backdrop');
  
  if (menuPanel) {
    // Set initial states
    gsap.set(menuPanel, { x: '-100%' });
    gsap.set([logo, closeButton], { opacity: 0, y: -20 });
    gsap.set(menuItems, { opacity: 0, x: -30 });
    gsap.set(backdrop, { opacity: 0 });
    
    // Animate backdrop fade in
    gsap.to(backdrop, {
      opacity: 1,
      duration: 0.3,
      ease: "power2.out"
    });
    
    // Animate panel sliding in
    gsap.to(menuPanel, { 
      x: '0%', 
      duration: 0.4, 
      ease: "power2.out" 
    });
    
    // Animate header elements
    gsap.to([logo, closeButton], { 
      opacity: 1, 
      y: 0, 
      duration: 0.3, 
      delay: 0.2, 
      ease: "back.out(1.7)" 
    });
    
    // Animate menu items with stagger
    gsap.to(menuItems, { 
      opacity: 1, 
      x: 0, 
      duration: 0.4, 
      delay: 0.3, 
      stagger: 0.05, 
      ease: "power2.out" 
    });
  }
}

// Function to animate mobile menu closing
function animateMobileMenuClose() {
  return new Promise((resolve) => {
    const menuPanel = document.querySelector('.mobile-menu-panel');
    const menuItems = document.querySelectorAll('.mobile-menu-item');
    const backdrop = document.querySelector('.mobile-menu-backdrop');
    
    if (menuPanel) {
      // Animate items out first
      gsap.to(menuItems, { 
        opacity: 0, 
        x: -30, 
        duration: 0.2, 
        stagger: 0.02, 
        ease: "power2.in" 
      });
      
      // Animate backdrop fade out
      gsap.to(backdrop, {
        opacity: 0,
        duration: 0.2,
        delay: 0.1,
        ease: "power2.in"
      });
      
      // Then slide panel out
      gsap.to(menuPanel, { 
        x: '-100%', 
        duration: 0.3, 
        delay: 0.1, 
        ease: "power2.in",
        onComplete: resolve
      });
    } else {
      resolve();
    }
  });
}

// Updated mobile menu handlers
const openMobileMenu = () => {
  mobileMenuOpen.value = true;
  // Small delay to ensure DOM is updated
  setTimeout(animateMobileMenuOpen, 10);
};

const closeMobileMenu = async () => {
  await animateMobileMenuClose();
  mobileMenuOpen.value = false;
};

// Add backdrop click handler
const handleBackdropClick = (event) => {
  // Only close if clicking directly on the backdrop, not on the panel
  if (event.target.classList.contains('mobile-menu-backdrop')) {
    closeMobileMenu();
  }
};
</script>

<template>
  <!-- Full-width header with no rounded corners or top spacing -->
  <header :class="[ 'fixed z-50 transition-all duration-700 ease-out w-full top-0 left-0 right-0', isScrolled ? 'py-2' : 'py-3' ]">
    <!-- Header background overlay with dynamic background based on section -->
    <div :class="[ 
      isScrolled ? 'opacity-100' : 'opacity-0',
      currentSectionBg === 'dark' ? 'bg-zinc-900/95 dark:bg-white/95' : 'bg-white/95 dark:bg-zinc-900/95',
      'absolute inset-0 shadow-md border-b transition-all duration-700 ease-out backdrop-blur-lg'
    ]"
    :style="{ 
      backdropFilter: `blur(${Math.min(16, scrollY / 10)}px)`, 
      WebkitBackdropFilter: `blur(${Math.min(16, scrollY / 10)}px)`,
      borderBottomColor: currentSectionBg === 'dark' ? 'rgb(255 255 255 / 0.3)' : 'rgb(161 161 170 / 0.3)'
    }">
    </div>
    
    <!-- Header content with container for max width -->
    <div class="relative z-10 w-full h-full max-w-screen-2xl mx-auto px-4 xl:px-8">
      <div class="flex sm:grid grid-cols-3 lg:flex items-center justify-between h-full">
        <!-- Logo - responsive positioning with dynamic colors -->
        <div class="flex-shrink-0 transition-all duration-300 order-1 sm:order-2 lg:order-1">
          <CustomLink href="/" class="block" :useInertia="true">
            <span class="sr-only">c-o-d-e-s software development</span>
            <img class="mb-1.5 md:mb-1 h-9 w-auto max-w-36 sm:max-w-44 md:max-w-none transition-all duration-300 sm:mx-auto" 
                 :src="currentSectionBg === 'dark' && isScrolled ? '/img/logo/codes_logo_tight_white.svg' : '/img/logo/codes_logo_tight.svg'" 
                 alt="c-o-d-e-s logo" />
          </CustomLink>
        </div>
        
        <!-- Mobile hamburger - responsive positioning with dynamic colors -->
        <div class="flex lg:hidden order-3 sm:order-1">
          <button type="button" :class="[
            'relative inline-flex items-center justify-center p-2 rounded-md focus:outline-none transition-colors duration-200',
            currentSectionBg === 'dark' && isScrolled ? 'text-white hover:text-zinc-200 hover:bg-white/10' : 'text-zinc-700 hover:text-black hover:bg-zinc-50'
          ]" @click="openMobileMenu">
            <span class="sr-only">Open menu</span>
            <font-awesome-icon :icon="['fas', 'bars']" class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        
        <!-- Navigation - hidden on mobile/tablet, centered on desktop with dynamic colors -->
        <div class="hidden lg:flex lg:items-center lg:justify-center order-2">
          <!-- Popover group for navigation items -->
          <PopoverGroup class="flex items-center justify-center space-x-6 py-2 px-4">
            <!-- Products dropdown with dynamic colors -->
            <Popover class="relative" v-slot="{ open, close }" @change="popoverOpen = open">
              <PopoverButton :class="[
                'group inline-flex items-center text-base font-medium py-2 focus:outline-none cursor-pointer transition-colors duration-300',
                currentSectionBg === 'dark' && isScrolled ? 'text-white hover:text-zinc-200' : 'text-zinc-900 dark:text-white hover:text-azure-700 dark:hover:text-azure-400'
              ]" @mouseenter="!popoverOpen && rotateChevron(90, 0.2)" @mouseleave="!popoverOpen && rotateChevron(0, 0.2)">
                <span>Projecten</span>
                <font-awesome-icon ref="chevronRef" :icon="['fas', 'chevron-down']" :class="[
                  'ml-1 h-3 w-3 flex-none',
                  currentSectionBg === 'dark' && isScrolled ? 'text-white' : 'text-zinc-900 dark:text-white'
                ]" aria-hidden="true" />
              </PopoverButton>
              
              <!-- Products panel with smoother slide-down animation -->
              <transition enter-active-class="transition-all duration-400 ease-out" enter-from-class="opacity-0 -translate-y-6" enter-to-class="opacity-100 translate-y-0" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 -translate-y-2" @enter="handlePopoverEnter">
                <PopoverPanel class="fixed left-1/2 z-10 mt-5 flex w-full max-w-7xl -translate-x-1/2 px-4">
                  <div class="mx-auto w-full flex-auto overflow-hidden rounded-2xl bg-white/90 dark:bg-zinc-800/95 backdrop-blur-lg text-sm leading-6 shadow-lg ring-1 ring-zinc-900/10 dark:ring-zinc-700/30 transition-colors duration-300">
                    <div class="p-6 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
                      <!-- Product items - start invisible -->
                      <CustomLink v-for="product in headerProducts" :key="product.meta.id" :href="'/project/' + product.meta.slug" class="solution-item opacity-0 group relative flex gap-x-6 rounded-lg p-4 hover:bg-zinc-50/50 dark:hover:bg-zinc-700/50 transition-all duration-300 cursor-pointer" :useInertia="true" @click="close">
                        <div class="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-white dark:bg-zinc-700 shadow-md ring-1 ring-zinc-200 dark:ring-zinc-600 transition-all duration-300 group-hover:scale-110">
                          <font-awesome-icon :icon="['fas', product.meta.icon]" :class="`h-6 w-6 text-zinc-500 dark:text-zinc-400 group-hover:text-${getProductIconColor(product)} transition-all duration-300`" aria-hidden="true" />
                        </div>
                        <div>
                          <div class="font-semibold text-zinc-900 dark:text-white transition-all duration-500 ease-smooth group-hover:translate-x-2">
                            {{ product.meta.name }}
                          </div>
                          <p class="mt-1 text-zinc-600 dark:text-zinc-300 line-clamp-2 transition-all duration-500 ease-smooth group-hover:translate-x-2 group-hover:delay-[100ms]">
                            {{ product.content.subTitle }}
                          </p>
                        </div>
                      </CustomLink>
                    </div>
                    
                    <!-- Call to action section - start invisible -->
                    <div class="bg-zinc-50 dark:bg-zinc-700/50 grid grid-cols-2 divide-x divide-zinc-900/5 dark:divide-zinc-600/20">
                      <CustomLink v-for="item in callsToAction" :key="item.name" :href="item.href" class="popover-cta-item opacity-0 group flex items-center justify-center gap-x-2.5 p-4 font-semibold text-zinc-900 dark:text-white hover:bg-zinc-100/50 dark:hover:bg-zinc-600/30 transition-all duration-300 cursor-pointer" :useInertia="true">
                        {{ item.name }}
                        <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-3 w-3 flex-none text-zinc-500 dark:text-zinc-400 transition-transform duration-300 group-hover:translate-x-1" />
                      </CustomLink>
                    </div>
                  </div>
                </PopoverPanel>
              </transition>
            </Popover>
            
            <!-- Regular navigation links with dynamic colors -->
            <CustomLink href="/#software-solutions" :class="[
              'text-base font-medium py-2 transition-colors duration-300',
              currentSectionBg === 'dark' && isScrolled ? 'text-white hover:text-zinc-200' : 'text-zinc-900 dark:text-white hover:text-azure-700 dark:hover:text-azure-400'
            ]">Mogelijkheden</CustomLink>
            <CustomLink href="/#platform-features" :class="[
              'text-base font-medium py-2 transition-colors duration-300',
              currentSectionBg === 'dark' && isScrolled ? 'text-white hover:text-zinc-200' : 'text-zinc-900 dark:text-white hover:text-azure-700 dark:hover:text-azure-400'
            ]">Functionaliteit</CustomLink>
            <CustomLink href="/#client-testimonials" :class="[
              'text-base font-medium py-2 transition-colors duration-300',
              currentSectionBg === 'dark' && isScrolled ? 'text-white hover:text-zinc-200' : 'text-zinc-900 dark:text-white hover:text-azure-700 dark:hover:text-azure-400'
            ]">Klantervaringen</CustomLink>
          </PopoverGroup>
        </div>
        
        <!-- CTA Button - hidden on mobile, shown from sm+ -->
        <div class="hidden sm:flex items-center justify-end order-3">
          <Button href="/contact" variant="gradient">Ontdek jouw oplossing</Button>
        </div>
      </div>
    </div>
    
    <!-- Mobile menu dialog with enhanced animations -->
    <Dialog as="div" class="lg:hidden relative z-50" @close="closeMobileMenu" :open="mobileMenuOpen">
      <div class="mobile-menu-backdrop fixed inset-0 bg-black/30 backdrop-blur-sm cursor-pointer" aria-hidden="true" @click="handleBackdropClick" />
      
      <!-- Panel with custom animation -->
      <div class="mobile-menu-panel fixed inset-y-0 left-0 z-50 w-full h-full overflow-y-auto bg-white dark:bg-zinc-900 sm:max-w-sm sm:ring-1 sm:ring-zinc-900/10 dark:sm:ring-zinc-700/30 shadow-2xl transition-colors duration-300 flex flex-col">
        <div class="flex items-center justify-between p-6">
          <!-- Logo in mobile menu -->
          <CustomLink href="/" class="mobile-menu-logo -m-1.5 p-1.5" :useInertia="true" @click="closeMobileMenu">
            <span class="sr-only">c-o-d-e-s software development</span>
            <img class="h-8 w-auto" :src="'/img/logo/codes_logo_tight.svg'" alt="c-o-d-e-s logo" />
          </CustomLink>
          
          <!-- Close button with hover animation -->
          <button type="button" class="mobile-menu-close -m-2.5 rounded-md p-2.5 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white transition-all duration-200 hover:scale-110" @click="closeMobileMenu">
            <span class="sr-only">Close menu</span>
            <font-awesome-icon :icon="['fas', 'xmark']" class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        
        <!-- Mobile menu content - flex-1 to take remaining space -->
        <div class="flex-1 mt-6 flow-root px-6">
          <div class="-my-6 divide-y divide-zinc-500/10">
            <div class="space-y-4 py-6">
              <!-- Products dropdown in mobile menu -->
              <Disclosure as="div" class="mobile-menu-item -mx-3" v-slot="{ open }">
                <DisclosureButton @click="handleProductsButtonClick()" class="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-zinc-900 dark:text-white hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-all duration-200 hover:translate-x-1 cursor-pointer">
                  Projecten
                  <font-awesome-icon :icon="['fas', 'chevron-down']" :class="[open ? 'rotate-180' : '', 'h-5 w-5 flex-none text-zinc-900 dark:text-white transition-transform duration-300']" aria-hidden="true" />
                </DisclosureButton>
                
                <!-- Mobile dropdown panel with smooth transition -->
                <transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 max-h-0 -translate-y-2" enter-to-class="opacity-100 max-h-96 translate-y-0" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100 max-h-96 translate-y-0" leave-to-class="opacity-0 max-h-0 -translate-y-2">
                  <DisclosurePanel class="mt-2 space-y-2 overflow-hidden">
                    <CustomLink v-for="(product, index) in headerProducts" :key="product.meta.id" :href="'/project/' + product.meta.slug" class="mobile-product-item opacity-0 block rounded-lg py-2 pl-6 pr-3 text-base font-medium text-zinc-900 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-all duration-200 hover:translate-x-1" @click="closeMobileMenu">
                      <div class="flex items-center gap-3">
                        <font-awesome-icon :icon="['fas', product.meta.icon]" :class="`h-5 w-5 text-${getProductIconColor(product)}`" />
                        {{ product.meta.name }}
                      </div>
                    </CustomLink>
                    
                    <!-- Mobile CTA links -->
                    <div class="mobile-cta-item border-t border-zinc-200 dark:border-zinc-600 pt-4 mt-4">
                        <CustomLink v-for="item in callsToAction" :key="item.name" :href="item.href" class="mobile-cta-item block rounded-lg py-2 pl-6 pr-3 text-base font-medium text-zinc-900 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-all duration-200 hover:translate-x-1" @click="closeMobileMenu">
                        {{ item.name }}
                        </CustomLink>
                    </div>
                  </DisclosurePanel>
                </transition>
              </Disclosure>
              
                <div class="border-t border-zinc-200 dark:border-zinc-600 space-y-2 pt-4 -mx-3">
                    <!-- Regular mobile navigation links -->
                    <CustomLink href="/#software-solutions" class="mobile-menu-item block rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-zinc-900 dark:text-white hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-all duration-200 hover:translate-x-1" @click="closeMobileMenu">
                        Mogelijkheden
                    </CustomLink>
                    <CustomLink href="/#platform-features" class="mobile-menu-item block rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-zinc-900 dark:text-white hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-all duration-200 hover:translate-x-1" @click="closeMobileMenu">
                        Functionaliteit
                    </CustomLink>
                    <CustomLink href="/#client-testimonials" class="mobile-menu-item block rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-zinc-900 dark:text-white hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-all duration-200 hover:translate-x-1" @click="closeMobileMenu">
                        Klantervaringen
                    </CustomLink>
                </div>
            </div>
          </div>
        </div>
        
        <!-- Mobile CTA button - fixed to bottom -->
        <div class="mobile-menu-item p-6 border-t border-zinc-200 dark:border-zinc-700">
          <Button href="/contact" variant="gradient" class="w-full transform transition-all duration-200 hover:scale-105" @click="closeMobileMenu">
            Ontdek jouw oplossing
          </Button>
        </div>
      </div>
    </Dialog>
  </header>
</template>

<style>
/* Add explicit browser support for backdrop-filter */
@supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
  .backdrop-blur {
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
  }
}

/* Fallback for browsers that don't support backdrop-filter */
@supports not ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
  .backdrop-blur {
    background-color: rgba(255, 255, 255, 0.95) !important;
  }
}
</style>